import { ExternalLink, MapPin } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/common/PageHero";
import { PillLink } from "@/components/common/PillLink";
import { Reveal } from "@/components/common/Reveal";
import { LOCATIONS } from "@/lib/site";

const embedUrl = (q, zoom) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=${zoom}&output=embed`;
const openUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

const MapFrame = ({ query, zoom = 15, title, className = "aspect-[4/3]" }) => (
  <iframe
    title={title}
    src={embedUrl(query, zoom)}
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
    className={`block w-full border-0 ${className}`}
  />
);

const LocationCard = ({ loc, i }) => (
  <Reveal delay={i * 0.08} as="article">
    <div className="card-soft h-full overflow-hidden" data-testid={`location-card-${i}`}>
      <MapFrame query={loc.address} title={`Map of ${loc.name}`} />
      <div className="p-6">
        <h3 className="text-2xl font-semibold">{loc.name}</h3>
        <p className="mt-2 flex items-start gap-2 text-ink-soft">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {loc.address}
        </p>
        {loc.note && <p className="mt-2 text-sm text-ink-muted">{loc.note}</p>}
        <a href={openUrl(loc.address)} target="_blank" rel="noopener noreferrer" data-testid={`location-open-map-${i}`}
          className="mt-4 inline-flex items-center gap-1.5 font-bold underline-offset-4 hover:underline">
          Open in Google Maps <ExternalLink className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>
    </div>
  </Reveal>
);

const ComingSoon = () => (
  <div data-testid="locations-pending">
    <div className="mb-6 flex items-start gap-3 rounded-card bg-lemon p-5" role="status">
      <MapPin className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <p className="leading-relaxed">
        <span className="font-bold">Our location details are being added.</span> Send us an enquiry and we'll let you know
        the MUCCI location nearest to you.
      </p>
    </div>
    <div className="card-soft overflow-hidden">
      <MapFrame query="New Zealand" zoom={5} title="Map of New Zealand" className="h-[420px] sm:h-[520px]" />
    </div>
  </div>
);

export default function Locations() {
  useSeo("Locations", "Find MUCCI Academy locations for in-person inquiry-based classes in New Zealand.");
  return (
    <div data-testid="locations-page">
      <PageHero testid="locations-hero" eyebrow="At MUCCI locations" color="bg-lavender" title="Our locations"
        intro="Join MUCCI Academy classes in person. Find a location near you on the map." />
      <section className="container-x section-y" aria-labelledby="locations-title">
        <h2 id="locations-title" className="sr-only">Location maps</h2>
        {LOCATIONS.length ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {LOCATIONS.map((loc, i) => <LocationCard key={loc.name} loc={loc} i={i} />)}
          </div>
        ) : (
          <ComingSoon />
        )}
        <div className="mt-10 flex flex-wrap gap-3">
          <PillLink to="/contact#enquire" variant="lemon" arrow data-testid="locations-enquire">Enquire about in-person classes</PillLink>
          <PillLink to="/online-timetable" variant="mint" arrow data-testid="locations-online">Prefer online? See online courses</PillLink>
        </div>
      </section>
    </div>
  );
}
